/**
 * The floor (node security design D11, P0 item 16): answers no rule can loosen, because each
 * one is a way to run code or change what runs later without a person seeing it.
 *
 *   default:product-home      deny  writes into the Orgus home (keys, ceiling, hub data), reads
 *                                   of it, shell lines that name it, and the Orgus CLI commands
 *                                   that mint sign-in links or change the ceiling (RT-1); read
 *                                   tools may open <home>/attachments/ (delivered files)
 *   default:inline-code       ask   interpreters given code inline: node -e, python -c, sh -c,
 *                                   pwsh -EncodedCommand, cmd /c, osascript, eval, iex, ...
 *   default:protected-config  ask   writes to .git/hooks/, .git/config, .claude/, .codex/,
 *                                   .mcp.json, .husky/ and CI workflow files, places that start
 *                                   programs at sign-in (Startup folder, autostart, LaunchAgents,
 *                                   shell profiles), and git config keys that run programs
 *                                   (core.hooksPath, core.fsmonitor, ...)
 *   default:too-long          deny  a command line, path or session folder longer than the floor
 *                                   reads (FLOOR_TEXT_MAX, 32,768 characters): refused before
 *                                   any parsing, never cut and checked (containment review F4)
 *
 * evaluate() applies the floor after the rules: the stricter answer wins, so an org rule that
 * allows 'node *' still asks for 'node -e ...'. With full autonomy (ADR 0070) the floor's asks are
 * allowed; its deny stays. Pure and browser-safe, like the rest.
 */
import { FLOOR_TOO_LONG_REASON, commandReachesProtectedFolder, floorTextTooLong, pathInside, pathReachesProtectedFolder, trimEndOf } from '@orgus/protocol';
import type { PolicyRequest, PolicyResult, PolicyRule } from '@orgus/protocol';
import { programOf, splitCommand, subcommandOf } from './command.ts';
import { FILE_WRITE_TOOLS, READ_ONLY_TOOLS, SHELL_TOOLS, _internal } from './defaults.ts';
import { isAbsolutePath } from './glob.ts';

export interface FloorOptions {
  /** This machine's Orgus home folders (the agent passes its own); default names are always covered. */
  homes?: readonly string[];
}

const floorRule = (id: string, decision: PolicyRule['decision'], reason: string, match: PolicyRule['match']): PolicyRule => ({
  id,
  scope: { kind: 'org' },
  match,
  decision,
  reason,
  createdBy: 'orgus',
  createdAt: 0,
});

/** The floor as rules, for display beside DEFAULT_RULES. Rules never outrank these. */
export const FLOOR_RULES: readonly PolicyRule[] = [
  floorRule('default:product-home', 'deny', 'It reads or changes the Orgus folder, which holds this machine\'s keys, sign-in sessions and ceiling. Do that on the machine itself.', { pattern: '~/.orgus/**' }),
  floorRule('default:inline-code', 'ask', 'It runs code given inline to an interpreter, which no rule can see into, so a person decides.', { tool: 'Bash', pattern: 'node -e, python -c, sh -c, pwsh -EncodedCommand, ...' }),
  floorRule('default:protected-config', 'ask', 'It changes hooks, agent settings or CI files, which run code later, so a person decides.', { pattern: '.git/hooks/, .claude/, .codex/, CI workflows' }),
  floorRule('default:too-long', 'deny', FLOOR_TOO_LONG_REASON, { pattern: 'over 32,768 characters' }),
];

const floorResult = (id: string): PolicyResult => {
  const r = FLOOR_RULES.find((x) => x.id === id)!;
  return { decision: r.decision, ruleId: r.id, reason: r.reason };
};

// ── Inline code ─────────────────────────────────────────────────────────────

const SHELLS = new Set(['sh', 'bash', 'zsh', 'dash', 'ksh', 'ash', 'fish', 'csh', 'tcsh', 'busybox']);
const PYTHONS = /^(python[0-9.]*|py|pypy[0-9.]*)$/;
const EVAL_FLAGS: Record<string, Set<string>> = {
  node: new Set(['-e', '--eval', '-p', '--print', '-pe']),
  nodejs: new Set(['-e', '--eval', '-p', '--print', '-pe']),
  bun: new Set(['-e', '--eval', '-p', '--print']),
  deno: new Set(['eval']),
  perl: new Set(['-e', '-E']),
  ruby: new Set(['-e']),
  php: new Set(['-r']),
  lua: new Set(['-e']),
  luajit: new Set(['-e']),
  rscript: new Set(['-e']),
};
const POWERSHELL_FLAGS = /^[-/](c|command|e|ec|enc|encodedcommand|encodedarguments|ea)(:.*)?$/i;
const ALWAYS_INLINE = new Set(['osascript', 'eval', 'iex', 'invoke-expression', 'invoke-command', 'icm', 'start-job', 'wscript', 'cscript', 'mshta']);

function runsInlineCode(part: string): boolean {
  const { program, args } = programOf(part);
  const p = program.toLowerCase();
  if (!p) return false;
  if (ALWAYS_INLINE.has(p)) return true;
  const heredoc = /<<-?\s*['"]?\w/.test(part) || /<<<\s*\S/.test(part);
  if (SHELLS.has(p)) {
    // -c among other letters (-lc, -xc). Not /^-[A-Za-z]*c[A-Za-z]*$/: V8 backtracks that in
    // quadratic time on a long -ccc...c! (review F3); this reads the same.
    if (args.some((a) => /^-[A-Za-z]*$/.test(a) && a.includes('c'))) return true;
    return heredoc || args.length === 0 || args[0] === '-' || args[0] === '-s';
  }
  if (PYTHONS.test(p)) {
    if (args.some((a) => /^-[A-Za-z]*c$/.test(a))) return true;
    return heredoc || args[0] === '-';
  }
  if (p === 'pwsh' || p === 'powershell') return args.some((a) => POWERSHELL_FLAGS.test(a)) || args.length === 0 || heredoc;
  if (p === 'cmd') return args.some((a) => /^\/[ckr]$/i.test(a) || /^\/[ckr]\s/i.test(a));
  const flags = EVAL_FLAGS[p];
  if (flags) {
    if (args.some((a) => flags.has(a) || /^--(eval|print)=/.test(a))) return true;
    return heredoc || args[0] === '-';
  }
  return false;
}

// ── Protected paths ─────────────────────────────────────────────────────────

/** Path pieces (lower case, forward slashes, a leading '/') whose files run code later or configure an agent. */
const PROTECTED_SEGMENTS = [
  '/.git/hooks/',
  '/.husky/',
  '/.claude/',
  '/.codex/',
  '/.github/workflows/',
  '/.gitea/workflows/',
  '/.forgejo/workflows/',
  '/.circleci/',
  '/.buildkite/',
  // RT-1: what runs at the next sign-in.
  '/start menu/programs/startup/',
  '/.config/autostart/',
  '/library/launchagents/',
  '/.config/systemd/user/',
  '/documents/windowspowershell/',
  '/documents/powershell/',
];
const PROTECTED_FILES = [
  '/.git/config',
  '/.mcp.json',
  '/.gitlab-ci.yml',
  '/azure-pipelines.yml',
  '/jenkinsfile',
  '/.travis.yml',
  '/bitbucket-pipelines.yml',
  '/.drone.yml',
  '/.claude',
  '/.codex',
  '/.bashrc',
  '/.bash_profile',
  '/.profile',
  '/.zshrc',
  '/.zprofile',
  '/.zshenv',
  '/.config/fish/config.fish',
];

/** RT-1: the Orgus CLI commands that mint an owner sign-in link or change this machine's ceiling or control. */
const PRODUCT_CLI = /(^|[\s/"'])(orgus|orgus|flightdeck)(\.mjs|\.cmd|\.ps1|\.exe)?["']?\s+(token|login|agent\s+(ceiling|control|uninstall))(\s|$)/i;
/** git config keys whose value is a program git runs. */
const GIT_EXEC_KEYS = /^(core\.(hookspath|fsmonitor|sshcommand|editor|pager|askpass|gitproxy)|alias\..+|(diff|merge|filter)\..+\.(command|driver|clean|smudge|process|textconv)|credential\.helper|gpg\.program|sequence\.editor|uploadpack\.packobjectshook)$/i;

/**
 * A relative path joined to the session folder. The folder's trailing slashes go by a loop:
 * /\/+$/ takes quadratic time on a long run of slashes that does not reach the end (review F3).
 */
const joinCwd = (cwd: string, raw: string): string => `${trimEndOf(cwd.replace(/\\/g, '/'), '/')}/${raw}`;

function shape(p: string, cwd: string | undefined): string {
  let s = p.trim().replace(/\\/g, '/');
  if (!isAbsolutePath(s) && cwd && !s.startsWith('$') && !s.startsWith('%')) s = joinCwd(cwd, s);
  s = s.toLowerCase();
  return s.startsWith('/') ? s : `/${s}`;
}

/**
 * A checkout Claude Code keeps under .claude/worktrees/<name>/ is a working tree, not agent
 * settings: the path is judged as if the checkout were the repo (dogfood, 18 Oct 2026: once
 * mission worktrees moved there, every edit in one asked a person). Its own .claude/ stays protected.
 */
const CLAUDE_WORKTREE = /\/\.claude\/worktrees\/[^/]+\//g;

function isProtectedConfig(path: string, cwd: string | undefined): boolean {
  const s = shape(path, cwd).replace(CLAUDE_WORKTREE, '/');
  if (PROTECTED_SEGMENTS.some((x) => s.includes(x) || s.endsWith(x.slice(0, -1)))) return true;
  return PROTECTED_FILES.some((x) => s.endsWith(x));
}

function inHome(path: string, cwd: string | undefined, homes: readonly string[]): boolean {
  const raw = path.trim();
  const abs = isAbsolutePath(raw) || !cwd ? raw : joinCwd(cwd, raw);
  if (homes.some((h) => h && !raw.startsWith('~') && isAbsolutePath(abs) && pathInside(abs, h))) return true;
  return pathReachesProtectedFolder(raw, homes, cwd) !== null;
}

function gitRunsProgram(part: string): boolean {
  const { program, args } = programOf(part);
  if (program.toLowerCase() !== 'git') return false;
  if (args.some((a, i) => (a === '-c' && GIT_EXEC_KEYS.test((args[i + 1] ?? '').split('=')[0] ?? '')) || /^--config-env=/.test(a))) return true;
  if (subcommandOf('git', args) !== 'config') return false;
  return args.some((a) => GIT_EXEC_KEYS.test(a));
}

/**
 * RT-1: a path a read tool names that lies in an Orgus home (absolute, or ~/.orgus). The home's
 * attachments folder is the exception: the agent delivers a mission's or a fix dispatch's files
 * there for the sessions it launches to read (permissions.additionalDirectories).
 */
function readsHome(subject: string, cwd: string | undefined, homes: readonly string[]): boolean {
  const raw = subject.trim();
  if (!raw || /[*?]/.test(raw.replace(/[/\\][^/\\]*$/, ''))) return false;
  const abs = isAbsolutePath(raw) || !cwd ? raw : joinCwd(cwd, raw);
  if (homes.some((h) => h && isAbsolutePath(abs) && pathInside(abs, `${trimEndOf(h, '/\\')}/attachments`))) return false;
  if (homes.some((h) => h && isAbsolutePath(abs) && pathInside(abs, h))) return true;
  return pathReachesProtectedFolder(raw, homes, cwd) !== null;
}

/** What the floor says about one simple command or one file write, or null when it has nothing to say. */
function floorOne(req: PolicyRequest, subject: string, homes: readonly string[]): PolicyResult | null {
  const tool = (req.tool ?? '').toLowerCase();
  if (SHELL_TOOLS.has(tool)) {
    const targets = _internal.writeTargets(subject);
    if (targets.some((t) => inHome(t, req.cwd, homes))) return floorResult('default:product-home');
    // RT-1: reading the home is as bad as writing it (its keys sign commands), and the CLI's
    // token, ceiling and control commands change who may do what.
    if (commandReachesProtectedFolder(subject, homes, req.cwd) !== null || PRODUCT_CLI.test(subject)) return floorResult('default:product-home');
    if (runsInlineCode(subject)) return floorResult('default:inline-code');
    if (targets.some((t) => isProtectedConfig(t, req.cwd)) || gitRunsProgram(subject)) return floorResult('default:protected-config');
    return null;
  }
  if (FILE_WRITE_TOOLS.has(tool) && subject) {
    if (inHome(subject, req.cwd, homes)) return floorResult('default:product-home');
    if (isProtectedConfig(subject, req.cwd)) return floorResult('default:protected-config');
  }
  if (READ_ONLY_TOOLS.has(tool) && subject && readsHome(subject, req.cwd, homes)) return floorResult('default:product-home');
  return null;
}

/**
 * The floor's answer for a request, or null. A shell line is judged per simple command and
 * the strictest answer wins (deny over ask).
 */
export function floorDecision(req: PolicyRequest, opts: FloorOptions = {}): PolicyResult | null {
  if (!req.tool || req.subject == null) return null;
  const long = floorTooLong(req);
  if (long) return long;
  const homes = opts.homes ?? [];
  const tool = req.tool.toLowerCase();
  // A cd in one part moves where a later part's relative path points, so the whole line is
  // judged for the Orgus home too (cd ~ && cat .orgus/agent.json).
  if (SHELL_TOOLS.has(tool) && commandReachesProtectedFolder(req.subject, homes, req.cwd) !== null) return floorResult('default:product-home');
  const parts = SHELL_TOOLS.has(tool) ? splitCommand(req.subject) : [req.subject];
  let out: PolicyResult | null = null;
  for (const part of parts.length ? parts : [req.subject]) {
    const r = floorOne(req, part, homes);
    if (r && (!out || (r.decision === 'deny' && out.decision !== 'deny'))) out = r;
  }
  return out;
}

/**
 * default:too-long when a tool request's subject (a command line or path) or its session folder
 * is longer than the floor reads (FLOOR_TEXT_MAX), else null. Checked before anything parses
 * the request: evaluate() answers it before the rules too.
 */
export function floorTooLong(req: PolicyRequest): PolicyResult | null {
  return req.tool && (floorTextTooLong(req.subject) || floorTextTooLong(req.cwd)) ? floorResult('default:too-long') : null;
}

/** Exposed for tests. */
export const _floor = { runsInlineCode, isProtectedConfig, gitRunsProgram };
